import { LensTheme, StatusIndicator } from '@diligentcorp/atlas-theme-mui-lens';
import { Close } from '@mui/icons-material';
import Language from '@mui/icons-material/Language';
import { Box, Drawer, IconButton, Typography, useTheme } from '@mui/material';
import {
  DrawerHeader,
  LearnMoreButton,
  TranslationBar,
  TranslationToggleButton,
} from './ReportMessagesDrawer.styles';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CoiStatusColorMap } from 'src/components/atlas/organisms/helpers/CoiStatusColorMap';
import { SpeakUpStatusColorMap } from 'src/components/atlas/organisms/helpers/SpeakUpColorMap';
import { Modal } from 'src/components/molecules';
import { reportDetailsVm } from 'src/components/pages/Case/Case.slice';
import {
  ManagedChannelType,
  useGetChannelEntriesQuery,
} from 'src/graphql/generated';
import { useIsFeatureEnabled } from 'src/hooks';
import {
  CaseTypeEnum,
  FeatureFlag,
  getReadableIdWithDash,
  Message,
  State,
} from 'src/types';
import { capitaliseFirstLetter } from 'src/utils';
import { isBefore } from 'src/utils/time';
import {
  ModalDescription,
  ModalDescriptionBold,
} from '../ReportMessages/ReportMessages.styles';
import { useManagedChannel } from '../ReportMessages/useManagedChannel';
import { DrawerMessageInput } from './DrawerMessageInput';
import { DrawerMessagesList } from './DrawerMessagesList';

interface Props {
  open: boolean;
  setTranslateMessages: (translateMessage: boolean) => void;
  messages: Message[];
  closeMessages: () => void;
  sendMessage: (channel: string) => void;
  draftMessage: string;
  updateDraftMessage: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isAssignee: boolean;
  caseId: string;
  channel: string;
  isLoading: boolean;
  caseStatus?: string;
  caseType?: CaseTypeEnum;
}

export const ReportMessagesDrawer = ({
  open,
  setTranslateMessages,
  messages,
  closeMessages,
  sendMessage,
  draftMessage,
  updateDraftMessage,
  isAssignee,
  caseId,
  channel,
  isLoading: _isLoading,
  caseStatus,
  caseType,
}: Props) => {
  const { t } = useTranslation();
  const { tokens } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;
  const color = tokens.semantic.color;

  const currentUser = useSelector(
    (state: State) => state.globalUsers.currentUser,
  );
  const isDynamicTranslationsEnabled = useIsFeatureEnabled(
    FeatureFlag.DYNAMIC_TRANSLATIONS,
  );

  const [translateMessages, setLocalTranslate] = React.useState(
    currentUser?.translationEnabled ?? false,
  );
  const [openModal, setOpenModal] = React.useState(false);

  React.useEffect(() => {
    const enabled = currentUser?.translationEnabled ?? false;
    setLocalTranslate(enabled);
    setTranslateMessages(enabled);
  }, [open, currentUser?.translationEnabled, setTranslateMessages]);

  const handleToggle = () => {
    const next = !translateMessages;
    setLocalTranslate(next);
    setTranslateMessages(next);
  };

  const { data: channelEntriesData } = useGetChannelEntriesQuery(
    { channel },
    { enabled: open && Boolean(channel) },
  );

  const { channelEntries, hasSentMessage } = useManagedChannel(
    (channelEntriesData?.getChannelEntries as ManagedChannelType[]) ?? [],
  );

  const {
    permissions: { canViewReportMessages, canSendReportMessages },
  } = useSelector(reportDetailsVm({ reportId: channel }));

  const sortedMessagesAndNotifications = React.useMemo(
    () =>
      [
        ...messages.map((message) => ({
          ...message,
          type: 'message' as const,
        })),
        ...channelEntries,
      ].sort((a, b) => (isBefore(a.createdAt, b.createdAt) ? -1 : 1)),
    [messages, channelEntries],
  );

  if (!canViewReportMessages) {
    return null;
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={closeMessages}
        ModalProps={{ keepMounted: false }}
        data-testid="report-messages-drawer"
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: '450px' },
            backgroundColor: color.surface.default.value,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '100vh',
            padding: 0,
            gap: 0,
          },
        }}
      >
        <DrawerHeader>
          {/* Title bar */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: sp[3].value,
              py: sp[2].value,
              borderBottom: `1px solid ${color.ui.divider.default.value}`,
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: '20px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
              }}
            >
              {caseType === CaseTypeEnum.COI
                ? 'Message employee'
                : 'Message reporter'}
            </Typography>
            <IconButton
              onClick={closeMessages}
              aria-label="Close message drawer"
              data-testid="report-messages-drawer-close"
              sx={{
                color: color.action.secondary.onSecondary.value,
                '&:hover': {
                  backgroundColor: color.action.secondary.hoverFill.value,
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Case sub-header: reference + status chip */}
          {(caseId || caseStatus) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                px: sp[3].value,
                py: sp[1].value,
                flexShrink: 0,
              }}
            >
              {caseId && (
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    letterSpacing: '0.2px',
                    color: color.type.default.value,
                  }}
                >
                  {caseType === CaseTypeEnum.COI ? 'Disclosure' : 'Case'}{' '}
                  {getReadableIdWithDash(caseId).toUpperCase()}
                </Typography>
              )}
              {caseStatus && (
                <StatusIndicator
                  label={capitaliseFirstLetter(caseStatus)}
                  size="small"
                  customColor={
                    caseType === CaseTypeEnum.COI
                      ? CoiStatusColorMap(caseStatus)
                      : SpeakUpStatusColorMap(caseStatus)
                  }
                />
              )}
            </Box>
          )}
        </DrawerHeader>

        {/* Translation bar */}
        {isDynamicTranslationsEnabled && (
          <TranslationBar
            sx={{ px: sp[3].value, py: sp[1].value }}
            data-testid="translation-bar"
          >
            <TranslationToggleButton
              variant="text"
              size="small"
              onClick={handleToggle}
              data-testid="reshub-message-translation-toggle"
              startIcon={
                <Language
                  sx={{ fontSize: '18px', color: color.type.muted.value }}
                />
              }
              sx={{ color: color.type.default.value }}
            >
              {translateMessages
                ? t('public.ChatContainer.showOriginal', 'Show original')
                : t(
                    'public.ChatContainer.autoTranslateToggle',
                    'Auto-translate',
                  )}
            </TranslationToggleButton>
            <LearnMoreButton
              variant="text"
              size="small"
              onClick={() => setOpenModal(true)}
              data-testid="reshub-message-translation-learn-more-button"
              sx={{ color: color.type.default.value }}
            >
              {t('public.ChatContainer.learnMore', 'Learn more')}
            </LearnMoreButton>
          </TranslationBar>
        )}

        {/* Messages list */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <DrawerMessagesList messagesList={sortedMessagesAndNotifications} />
        </Box>

        {/* Message input */}
        {canSendReportMessages && (
          <Box sx={{ flexShrink: 0 }}>
            <DrawerMessageInput
              updateDraftMessage={updateDraftMessage}
              sendMessage={() => sendMessage(channel)}
              draftMessage={draftMessage}
              isAssigneeOrHasSentMessage={isAssignee || hasSentMessage}
            />
          </Box>
        )}
      </Drawer>
      {isDynamicTranslationsEnabled && (
        <Modal
          title={t('public.ChatContainer.titlev2')}
          setOpen={setOpenModal}
          open={openModal}
          onConfirm={() => setOpenModal(false)}
          size={'medium'}
          data-testid={'reshub-message-translation-learn-more-modal'}
          confirmButtonText={t('public.ChatContainer.understood')}
        >
          <ModalDescription>
            {t('public.ChatContainer.description')}
            <br />
            {t('public.ChatContainer.autoTranslate')}
            <br />
            {t('public.ChatContainer.changeInProfile')}{' '}
            <ModalDescriptionBold>
              {t('public.ChatContainer.caseSettings')}
            </ModalDescriptionBold>
            <br />
            <br />
            {t('public.ChatContainer.descriptionSecondHalf')}
          </ModalDescription>
        </Modal>
      )}
    </>
  );
};
